/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { Row, Cell, TextField } from 'sarsaparilla';
import SelectInput from './inputs/SelectInput';
import { PassTypeProps, WatercraftInfoProps, WatercraftInfoDefaults } from '../props';
import { boatDescriptionRules } from '../constants';

export default class WatercraftInfoForm extends Component {
    static defaultProps = {
        watercraftInfo: WatercraftInfoDefaults,
        isScannedPass: false,
        skipCustomerInfo: false,
    };

    static propTypes = {
        isScannedPass: PropTypes.bool,
        skipCustomerInfo: PropTypes.bool,
        number: PropTypes.number,
        submitAttempted: PropTypes.bool,
        watercraftInfo: WatercraftInfoProps,
        passType: PassTypeProps,
        onChange: PropTypes.func,
        reservationId: PropTypes.string,
    };

    constructor(props) {
        super(props);

        this.state = {
            watercraftType: this.props.watercraftInfo.type,
            licenseNumber: get(this.props, 'watercraftInfo.fields.license', ''),
            equipDescription: get(
                this.props,
                'watercraftInfo.fields.equip_description',
                ''
            ),
            boatDescriptionStatus: boatDescriptionRules[this.props.watercraftInfo.type],
        };
    }

    componentDidMount() {
        const { reservationId } = this.props;
        const initialSelection = get(
            this.props,
            'passType.features.equipment-info.watercraft',
            {}
        );
        if (!isEmpty(initialSelection) && reservationId?.length < 4) {
            if (Object.keys(initialSelection).length > 1) {
                this.onWatercraftTypeChanged('');
                return;
            }
            if (Object.keys(initialSelection).length === 1) {
                const selection = Object.keys(initialSelection)[0] || '';
                this.onWatercraftTypeChanged(selection);
                return;
            }
        }
        this.props.onChange(
            {
                ...this.props.watercraftInfo,
                fields: {
                    license: this.state.licenseNumber,
                    equip_description: this.state.equipDescription,
                },
            },
            this.formIsValid()
        );
    }

    componentWillUnmount() {
        this.props.onChange(this.props.watercraftInfo, true);
    }

    onWatercraftTypeChanged = (newWatercraftType) => {
        const { watercraftInfo, watercraftType, equipDescription, licenseNumber } =
            this.state;
        if (newWatercraftType !== watercraftType) {
            const newLicenseNumber = this.getDefaultLicenseNumber(newWatercraftType);
            const newEquipDescription = this.getDefaultBoatDescription(newWatercraftType);
            const licenseNum =
                newLicenseNumber !== licenseNumber ? newLicenseNumber : licenseNumber;
            const equipDesc =
                newEquipDescription !== equipDescription
                    ? newEquipDescription
                    : equipDescription;
            this.setState(
                {
                    watercraftType: newWatercraftType,
                    licenseNumber: licenseNum,
                    equipDescription: equipDesc,
                    boatDescriptionStatus: boatDescriptionRules[newWatercraftType],
                },
                () => {
                    this.props.onChange(
                        {
                            ...watercraftInfo,
                            type: newWatercraftType,
                            fields: { license: licenseNum, equip_description: equipDesc },
                        },
                        this.formIsValid()
                    );
                }
            );
        }
    };

    onLicenseNumberChanged = (licenseNumber) => {
        const { watercraftInfo } = this.props;

        this.setState(
            (prevState) => ({ ...prevState, licenseNumber }),
            () => {
                this.props.onChange(
                    {
                        ...watercraftInfo,
                        fields: { ...watercraftInfo?.fields, license: licenseNumber },
                    },
                    this.formIsValid()
                );
            }
        );
    };

    onBoatDescriptionChanged = (equipDescription) => {
        const { watercraftInfo } = this.props;

        this.setState(
            (prevState) => ({ ...prevState, equipDescription }),
            () => {
                this.props.onChange(
                    {
                        ...watercraftInfo,
                        fields: {
                            ...watercraftInfo?.fields,
                            equip_description: equipDescription,
                        },
                    },
                    this.formIsValid()
                );
            }
        );
    };

    getDefaultLicenseNumber = (watercraftType) => {
        const { license_required } = get(
            this.props,
            `passType.features['equipment-info']['watercraft']['${watercraftType}']`,
            {}
        );
        return license_required ? '' : 'N/A';
    };

    getDefaultBoatDescription = (watercraftType) => {
        const currentRule = boatDescriptionRules[watercraftType];
        return currentRule !== 'N/A' ? '' : currentRule;
    };

    isMissingLicenseNumber(number, watercraftType) {
        const licenseRequired = get(this.props.passType, [
            'features',
            'equipment-info',
            'watercraft',
            watercraftType,
            'license_required',
        ]);

        if (!licenseRequired) {
            return false;
        }

        return number === '';
    }

    isMissingBoatDescription(description, watercraftType) {
        const currentRule = boatDescriptionRules[watercraftType];

        if (currentRule !== 'required') {
            return false;
        }

        return description === '';
    }

    formIsValid() {
        const { watercraftType, licenseNumber, equipDescription } = this.state;
        return (
            !this.isMissingLicenseNumber(licenseNumber, watercraftType) ||
            !this.isMissingBoatDescription(equipDescription, watercraftType)
        );
    }

    render() {
        const { isScannedPass, number, passType, submitAttempted, skipCustomerInfo } =
            this.props;
        const watercraftTypes = get(passType, [
            'features',
            'equipment-info',
            'watercraft',
        ]);
        if (!watercraftTypes) {
            return null;
        }
        let { watercraftType } = this.state;
        if (!watercraftType) {
            watercraftType = Object.keys(watercraftTypes)[0];
        }
        this.onWatercraftTypeChanged(watercraftType);
        const { licenseNumber, equipDescription, boatDescriptionStatus } = this.state;

        const licenseRequired = get(
            watercraftTypes,
            `${watercraftType}.license_required`
        );

        const isRequiredBoatDescription = boatDescriptionStatus === 'required';
        const isDisabledBoatDescription = boatDescriptionStatus === 'N/A';

        return (
            <Row className={isScannedPass ? 'scanned-pass' : ''}>
                <Cell
                    width={{
                        sm: 12,
                        md: 6,
                    }}
                >
                    <SelectInput
                        required={!skipCustomerInfo}
                        label="Boat Type"
                        id={`watercraft-type-${number}`}
                        value={skipCustomerInfo ? '' : watercraftType}
                        options={Object.entries(watercraftTypes).map(
                            ([value, { name: label }]) => ({ label, value })
                        )}
                        onChange={this.onWatercraftTypeChanged}
                        disabled={isScannedPass || skipCustomerInfo}
                    />
                </Cell>
                <Cell
                    width={{
                        sm: 12,
                        md: 6,
                    }}
                >
                    <TextField
                        isRequired={skipCustomerInfo ? false : licenseRequired}
                        label="Boat Registration Number"
                        id={`watercraft-license-${number}`}
                        value={skipCustomerInfo ? '' : licenseNumber}
                        disabled={isScannedPass || skipCustomerInfo || !licenseRequired}
                        shouldValidateOnUpdate={submitAttempted}
                        onChange={(e) => this.onLicenseNumberChanged(e.target.value)}
                    />
                </Cell>
                <Cell width={12}>
                    <TextField
                        isRequired={skipCustomerInfo ? false : isRequiredBoatDescription}
                        className="mb-3"
                        label="Boat Description"
                        id={`watercraft-description-${number}`}
                        value={skipCustomerInfo ? '' : equipDescription}
                        maxLength={70}
                        disabled={
                            isScannedPass ||
                            skipCustomerInfo ||
                            isDisabledBoatDescription ||
                            isEmpty(watercraftType)
                        }
                        shouldValidateOnUpdate={submitAttempted}
                        onChange={(e) => this.onBoatDescriptionChanged(e.target.value)}
                    />
                </Cell>
            </Row>
        );
    }
}
